import {
  mdiDelete,
  mdiEyeOutline,
  mdiFile,
  mdiFileImage,
  mdiFilePdfBox,
  mdiPencilOutline,
  mdiOpenInNew
} from "@mdi/js";
import Icon from "@mdi/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import Select from "components/Select";
import Modal from "components/Modal";
import { UPLOAD_CATEGORY } from "constants/base";
import { VISIBILITY_LIST } from "constants/haulpay-broker";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { IOption } from "models/Form";
import { IFRAttachment } from "models/HaulPay";
import { deleteFRAttachmentAPI, updateAttachment } from "services/api/haulpay";

import { useFundingRequestForm } from "./FundingRequestFormContext";
import MultiSelect from "components/MultiSelect";
import { useModal } from "context/ModalContext";
import { useUserFeature } from "hooks/useUserFeature";

const getFileIcon = (filename: string = "") => {
  const extension = filename.split(".").pop()?.toLowerCase();

  if (extension === "pdf") {
    return mdiFilePdfBox;
  }

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "")) {
    return mdiFileImage;
  }

  return mdiFile;
};

const normalizeCategoryValue = (val: string): string => {
  const raw = String(val ?? "").trim();
  if (!raw) return "";
  const match = UPLOAD_CATEGORY.find(
    (opt) => String(opt.value).toLowerCase() === raw.toLowerCase()
  );
  return (match?.value as string) || raw;
};

const stringifyCategory = (category: IFRAttachment["category"]): string => {
  if (Array.isArray(category)) return category.map(normalizeCategoryValue).join(",");
  return normalizeCategoryValue(String(category ?? ""));
};

const visibilityOptionFromAttachment = (attachment: IFRAttachment): IOption => {
  const visibilityArray = [...(attachment.visibleTo ?? attachment.visible_to ?? [])];

  // Normalize: if debtor is present without shipper, add shipper (and vice versa)
  const hasDebtor = visibilityArray.includes("debtor");
  const hasShipper = visibilityArray.includes("shipper");

  if (hasDebtor && !hasShipper) {
    visibilityArray.push("shipper");
  } else if (hasShipper && !hasDebtor) {
    visibilityArray.push("debtor");
  }

  // Find matching option by comparing array contents
  const match = VISIBILITY_LIST.find((opt) => {
    if (!opt.value) return false;
    const optionValues = opt.value.split(",");
    return (
      optionValues.length === visibilityArray.length &&
      optionValues.every((val) => visibilityArray.includes(val))
    );
  });

  return match ?? VISIBILITY_LIST[0];
};

const categoryOptionFromAttachment = (attachment: IFRAttachment): IOption[] | undefined => {
  const rawCategory = stringifyCategory(attachment.category);
  if (!rawCategory) return undefined;

  // Split the raw category string into an array of category values
  const categoryValues = rawCategory
    .split(",")
    .map((cat) => cat.trim())
    .filter(Boolean);
  if (!categoryValues.length) return undefined;

  // Find matching options for all categories
  const options = categoryValues
    .map((categoryValue) =>
      UPLOAD_CATEGORY.find((opt) => String(opt.value).toLowerCase() === categoryValue.toLowerCase())
    )
    .filter(Boolean) as IOption[]; // Filter out any undefined values

  return options.length ? options : undefined;
};

interface AttachmentRowProps {
  attachment: IFRAttachment;
  disabled: boolean;
  isDeleting: boolean;
  fundingRequestId?: string;
  onDelete: (id: string) => void;
  onUpdate: (id: string, patch: Partial<IFRAttachment>) => void;
  onEdit?: (attachment: IFRAttachment) => void;
  isInModal?: boolean;
}

function AttachmentRow({
  attachment,
  disabled,
  isDeleting,
  fundingRequestId,
  onDelete,
  onUpdate,
  onEdit,
  isInModal = false
}: Readonly<AttachmentRowProps>) {
  const { isCarrier } = useUserFeature();
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [updatingCategory, setUpdatingCategory] = useState(false);
  const [visibilityOption, setVisibilityOption] = useState<IOption>(() =>
    visibilityOptionFromAttachment(attachment)
  );
  const [categoryOption, setCategoryOption] = useState<IOption[] | undefined>(() =>
    categoryOptionFromAttachment(attachment)
  );

  useEffect(() => {
    setVisibilityOption(visibilityOptionFromAttachment(attachment));
    setCategoryOption(categoryOptionFromAttachment(attachment));
  }, [attachment]);
  const previewUrl = attachment.preview_url ?? attachment.previewUrl ?? attachment.downloadUrl;

  const handlePreview = useCallback(() => {
    if (!previewUrl) return;
    const newWindow = window.open(previewUrl, "_blank");
    if (!newWindow) {
      showErrorMessage("Please allow popups to preview this attachment.");
    } else {
      newWindow.focus();
    }
  }, [previewUrl]);

  const handleVisibilityChange = useCallback(
    async (option: IOption) => {
      const previous = visibilityOption;
      setVisibilityOption(option);

      const visibleList = option.value ? option.value.split(",") : [];

      if (!fundingRequestId) {
        onUpdate(attachment.id, { visibleTo: visibleList, visible_to: visibleList });
        return;
      }

      setUpdatingVisibility(true);
      try {
        await updateAttachment<IFRAttachment>({
          attachment: {
            ...attachment,
            category: stringifyCategory(attachment.category),
            visible_to: visibleList
          },
          fundingRequestId,
          attachmentId: attachment.id
        });
        onUpdate(attachment.id, { visibleTo: visibleList, visible_to: visibleList });
        showSuccessMessage("Visibility updated.");
      } catch (error) {
        setVisibilityOption(previous);
        showErrorMessage("Failed to update visibility.");
      } finally {
        setUpdatingVisibility(false);
      }
    },
    [attachment, fundingRequestId, onUpdate, visibilityOption]
  );

  const handleLocalCategoryChange = useCallback((options: IOption[]) => {
    setCategoryOption(options);
  }, []);

  const handleCategoryChange = useCallback(
    async (options: IOption[]) => {
      // Create a comma-separated string of all selected values
      const categoryValues = options.map((opt) => opt.value).join(",");

      if (!fundingRequestId) {
        onUpdate(attachment.id, { category: categoryValues });
        setIsEditingCategory(false);
        return;
      }

      const visibleList = visibilityOption.value ? visibilityOption.value.split(",") : [];
      const body: IFRAttachment = {
        ...attachment,
        category: categoryValues,
        visible_to: visibleList
      };
      if (isCarrier) delete body.visible_to;
      setUpdatingCategory(true);
      try {
        await updateAttachment<IFRAttachment>({
          attachment: body,
          fundingRequestId,
          attachmentId: attachment.id
        });
        onUpdate(attachment.id, { category: categoryValues });
        showSuccessMessage("Category updated.");
        setIsEditingCategory(false);
      } catch (error) {
        showErrorMessage("Failed to update category.");
      } finally {
        setUpdatingCategory(false);
      }
    },
    [attachment, fundingRequestId, isCarrier, onUpdate, visibilityOption]
  );

  const handleEditClick = useCallback(() => {
    // Check if it's an image or PDF
    const filename = attachment.filename || "";
    const extension = filename.split(".").pop()?.toLowerCase();
    const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "");
    const isPDF = extension === "pdf";

    if ((isImage || isPDF) && onEdit) {
      // For images, trigger the crop modal; for PDFs, trigger the document modal
      onEdit(attachment);
    } else {
      // For other file types, toggle inline category editing
      setIsEditingCategory((prev) => !prev);
    }
  }, [attachment, onEdit]);

  // Actions component to be reused
  const ActionButtons = () => (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={handlePreview}
        className="flex items-center justify-center min-w-[36px] min-h-[36px] rounded-full text-primary hover:text-primary-300 hover:bg-primary/10 disabled:opacity-50"
        disabled={!previewUrl}
        title="View file">
        <Icon path={mdiEyeOutline} size={0.85} />
      </button>

      <button
        type="button"
        onClick={handleEditClick}
        className="flex items-center justify-center min-w-[36px] min-h-[36px] rounded-full text-primary hover:text-primary-300 hover:bg-primary/10 disabled:opacity-50"
        disabled={disabled}
        title={isEditingCategory ? "Close" : "Edit"}>
        <Icon path={mdiPencilOutline} size={0.85} />
      </button>

      {!disabled && (
        <button
          type="button"
          onClick={() => onDelete(attachment.id)}
          disabled={isDeleting}
          className="flex items-center justify-center min-w-[36px] min-h-[36px] rounded-full text-error transition-colors hover:bg-error/10 disabled:opacity-50"
          title="Delete attachment">
          <Icon path={mdiDelete} size={0.85} className={isDeleting ? "animate-pulse" : ""} />
        </button>
      )}
    </div>
  );

  // Use the modal styling when isInModal is true
  if (isInModal) {
    return (
      <div className="flex flex-col gap-3 rounded-md border border-card p-3 desktop:flex-row desktop:items-center desktop:justify-between">
        <div className="flex items-start gap-3">
          <Icon path={getFileIcon(attachment.filename)} size={1} className="text-primary" />
          <div className="text-sm">
            <div className="max-w-[220px] truncate font-medium desktop:max-w-[260px]">
              {attachment.filename || "Unnamed file"}
            </div>
            {!isEditingCategory && (
              <div className="max-w-[240px] truncate text-xs text-placeholder desktop:max-w-[300px]">
                {Array.isArray(attachment.category)
                  ? attachment.category.join(", ")
                  : stringifyCategory(attachment.category) || "No category"}
              </div>
            )}

            {isEditingCategory && (
              <div className="mt-2 max-w-[300px]">
                <MultiSelect
                  options={UPLOAD_CATEGORY}
                  value={categoryOption || []}
                  onChange={handleLocalCategoryChange}
                  disabled={disabled || updatingCategory}
                  className="text-xs"
                  btnClass="!py-1 !px-2 !text-xs"
                  onDropdownClose={handleCategoryChange}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 self-end desktop:self-auto">
          {!isCarrier && (
            <Select
              options={VISIBILITY_LIST}
              value={visibilityOption}
              onChange={(opt) => opt && handleVisibilityChange(opt)}
              disabled={disabled || updatingVisibility}
              className="text-xs min-w-40"
              btnClass="!py-2 !pl-3 !pr-8 !text-xs !bg-transparent !border-card"
              arrowClass="!size-4"
            />
          )}

          <ActionButtons />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-card bg-card p-4
    shadow-[0_6px_12px_rgba(0,0,0,0.12)]
    transform translate-y-[-2px]
    transition-all duration-200 w-full">
      <div className="flex items-start w-full">
        <div className="flex items-start gap-2 w-full min-w-0">
          <div className="w-6 shrink-0">
            <Icon
              path={getFileIcon(attachment.filename)}
              className="flex-1 text-primary !h-6 !w-6"
            />
          </div>
          <div className="text-sm flex-1 min-w-0">
            <div className="truncate font-medium min-w-0">
              {attachment.filename || "Unnamed file"}
            </div>

            {!isEditingCategory && (
              <div className="truncate text-xs text-placeholder mt-1 min-w-0">
                {Array.isArray(attachment.category)
                  ? attachment.category.join(", ")
                  : stringifyCategory(attachment.category) || "No category"}
              </div>
            )}

            {isEditingCategory && (
              <div className="mt-2 w-full">
                <MultiSelect
                  options={UPLOAD_CATEGORY}
                  value={categoryOption || []}
                  onChange={handleLocalCategoryChange}
                  disabled={disabled || updatingCategory}
                  className="text-xs"
                  btnClass="!py-1 !px-2 !text-xs"
                  onDropdownClose={handleCategoryChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Visibility and Actions row underneath */}
      <div className="flex items-center justify-between border-t border-card/50 pt-2 mt-1">
        {!isCarrier && (
          <Select
            options={VISIBILITY_LIST}
            value={visibilityOption}
            onChange={(opt) => opt && handleVisibilityChange(opt)}
            disabled={disabled || updatingVisibility}
            className="text-xs min-w-40"
            btnClass="!py-2 !pl-3 !pr-8 !text-xs !bg-transparent !border-card"
            arrowClass="!size-4"
          />
        )}
        <ActionButtons />
      </div>
    </div>
  );
}

export default function AttachmentsListCard() {
  const {
    attachments,
    handleAttachmentsChange,
    allowDocsUpdate,
    loading,
    selectedFR,
    setIsAttachmentsListIsModalOpen,
    isAttachmentListModalOpen,
    setEditingAttachment
  } = useFundingRequestForm();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { openModal, closeModal } = useModal();
  const hasAttachments = useMemo(
    () => Array.isArray(attachments) && attachments.length > 0,
    [attachments]
  );

  const handleAttachmentUpdate = useCallback(
    (id: string, patch: Partial<IFRAttachment>) => {
      const updated = attachments.map((attachment) =>
        attachment.id === id
          ? {
              ...attachment,
              ...patch,
              visibleTo: patch.visibleTo ?? attachment.visibleTo,
              visible_to: patch.visible_to ?? attachment.visible_to
            }
          : attachment
      );
      handleAttachmentsChange(updated as IFRAttachment[]);
    },
    [attachments, handleAttachmentsChange]
  );

  const handleDeleteAttachment = useCallback(
    async (attachmentId: string) => {
      if (!allowDocsUpdate) return;

      try {
        setDeletingId(attachmentId);
        const updatedAttachments = attachments.filter((att) => att.id !== attachmentId);
        handleAttachmentsChange(updatedAttachments as IFRAttachment[]);
        await deleteFRAttachmentAPI(selectedFR?.id, attachmentId);
        showSuccessMessage("Attachment removed successfully");
      } catch (error) {
        showErrorMessage("Failed to remove attachment");
      } finally {
        setDeletingId(null);
      }
    },
    [attachments, handleAttachmentsChange, allowDocsUpdate, selectedFR?.id]
  );

  const handleConfirmDeleteAttachment = useCallback(
    (id: string) =>
      openModal("warning", {
        message: "Are you sure you want to delete this attachment?",
        onConfirm: () => {
          handleDeleteAttachment && handleDeleteAttachment(id);
          closeModal("warning");
        },
        onClose: () => closeModal("warning")
      }),
    [handleDeleteAttachment]
  );

  const handleEditAttachment = useCallback(
    (attachment: IFRAttachment) => {
      setEditingAttachment(attachment);
      // Close the modal if it's open
      setIsAttachmentsListIsModalOpen(false);
    },
    [setEditingAttachment, setIsAttachmentsListIsModalOpen]
  );

  const AttachmentList = ({ isInModal }: { isInModal: boolean }) => (
    <>
      {!hasAttachments ? (
        <div className="flex min-h-[100px] items-center justify-center text-sm text-placeholder">
          No attachments uploaded
        </div>
      ) : (
        <div className="space-y-2 overflow-y-auto">
          {attachments.map((attachment) => (
            <AttachmentRow
              key={attachment.id}
              attachment={attachment}
              disabled={!allowDocsUpdate}
              fundingRequestId={selectedFR?.id}
              isDeleting={deletingId === attachment.id}
              onDelete={handleConfirmDeleteAttachment}
              onUpdate={handleAttachmentUpdate}
              onEdit={handleEditAttachment}
              isInModal={isInModal}
            />
          ))}
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="rounded-lg border border-card bg-card p-3">
        <h3 className="mb-2 font-semibold text-primary">Attachments List</h3>
        <div className="animate-pulse flex flex-col gap-4">
          <div className="h-5 bg-foreground rounded-md"></div>
          <div className="h-5 bg-foreground rounded-md"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-card bg-card p-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold text-primary">Attachments List</h3>
          <button
            type="button"
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 focus:outline-none disabled:opacity-50"
            onClick={() => setIsAttachmentsListIsModalOpen(true)}
            disabled={!hasAttachments}
            title="Open attachments in dialog">
            <Icon path={mdiOpenInNew} size={0.75} />
            View All
          </button>
        </div>

        <AttachmentList isInModal={false} />
      </div>

      <Modal
        isOpen={isAttachmentListModalOpen}
        closeModal={() => ({})}
        wrapperClass="!max-w-4xl"
        dialogClass="!bg-foreground !p-6 !rounded-xl !max-h-[80vh] !overflow-hidden"
        closeClass="!top-4 !right-4 hidden"
        showCloseButton>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary">Attachments</h2>
          <button
            type="button"
            className="rounded-md px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10"
            onClick={() => setIsAttachmentsListIsModalOpen(false)}>
            Close
          </button>
        </div>

        <div
          className="max-h-[60vh] space-y-3 overflow-y-auto pr-1"
          style={{ pointerEvents: "auto" }}>
          <AttachmentList isInModal={true} />
        </div>
      </Modal>
    </>
  );
}
